/**
 * Returns the UTC offset in minutes for a given timezone at a specific date.
 */
const getUTCOffsetMinutes = (timeZone, date) => {
  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
    const parts = formatter.formatToParts(date);
    const get = (type) => parseInt(parts.find((p) => p.type === type)?.value, 10);
    const utcDate = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute"), get("second"));
    return (utcDate - date.getTime()) / 60000;
  } catch (e) {
    return 0;
  }
};

/**
 * Detects whether DST is currently active by comparing the current offset
 * to the January (winter) offset for a US Eastern reference.
 * If the current offset differs from January's, DST is in effect.
 */
const isDSTActive = () => {
  const now = new Date();
  const january = new Date(now.getFullYear(), 0, 15);
  const currentOffset = getUTCOffsetMinutes("America/New_York", now);
  const januaryOffset = getUTCOffsetMinutes("America/New_York", january);
  return currentOffset !== januaryOffset;
};

// Daylight Saving Time offsets (DST-active period)
const TIMEZONE_OPTIONS_DST = [
  { value: "Etc/GMT+12", label: "UTC-12 (UTC-12)" },
  { value: "Pacific/Pago_Pago", label: "Samoa (UTC-11)" },
  { value: "Pacific/Honolulu", label: "Hawaii (UTC-10)" },
  { value: "America/Anchorage", label: "Alaska (UTC-9)" },
  { value: "America/Los_Angeles", label: "Pacific (UTC-7)" },
  { value: "America/Phoenix", label: "Arizona (UTC-7)" },
  { value: "America/Denver", label: "Mountain (UTC-6)" },
  { value: "America/Chicago", label: "Central (UTC-5)" },
  { value: "America/New_York", label: "Eastern (UTC-4)" },
  { value: "America/Halifax", label: "Atlantic (UTC-3)" },
  { value: "America/Noronha", label: "Fernando de Noronha (UTC-2)" },
  { value: "Atlantic/Azores", label: "Azores (UTC+0)" },
  { value: "UTC", label: "UTC (UTC+0)" },
  { value: "Europe/London", label: "London (UTC+1)" },
  { value: "Europe/Berlin", label: "Central Europe (UTC+2)" },
  { value: "Europe/Athens", label: "Eastern Europe (UTC+3)" },
  { value: "Asia/Dubai", label: "UAE (UTC+4)" },
  { value: "Asia/Karachi", label: "Pakistan (UTC+5)" },
  { value: "Asia/Kolkata", label: "India (UTC+5:30)" },
  { value: "Asia/Dhaka", label: "Bangladesh (UTC+6)" },
  { value: "Asia/Yangon", label: "Myanmar (UTC+6:30)" },
  { value: "Asia/Bangkok", label: "Thailand (UTC+7)" },
  { value: "Asia/Shanghai", label: "China (UTC+8)" },
  { value: "Asia/Tokyo", label: "Japan (UTC+9)" },
  { value: "Australia/Sydney", label: "Sydney (UTC+11)" },
  { value: "Pacific/Fiji", label: "Fiji (UTC+12)" },
];

// Standard (non-DST / winter) offsets
const TIMEZONE_OPTIONS_NO_DST = [
  { value: "Etc/GMT+12", label: "UTC-12 (UTC-12)" },
  { value: "Pacific/Pago_Pago", label: "Samoa (UTC-11)" },
  { value: "Pacific/Honolulu", label: "Hawaii (UTC-10)" },
  { value: "America/Anchorage", label: "Alaska (UTC-9)" },
  { value: "America/Los_Angeles", label: "Pacific (UTC-8)" },
  { value: "America/Phoenix", label: "Arizona (UTC-7)" },
  { value: "America/Denver", label: "Mountain (UTC-7)" },
  { value: "America/Chicago", label: "Central (UTC-6)" },
  { value: "America/New_York", label: "Eastern (UTC-5)" },
  { value: "America/Halifax", label: "Atlantic (UTC-4)" },
  { value: "America/Noronha", label: "Fernando de Noronha (UTC-2)" },
  { value: "Atlantic/Azores", label: "Azores (UTC-1)" },
  { value: "UTC", label: "UTC (UTC+0)" },
  { value: "Europe/London", label: "London (UTC+0)" },
  { value: "Europe/Berlin", label: "Central Europe (UTC+1)" },
  { value: "Europe/Athens", label: "Eastern Europe (UTC+2)" },
  { value: "Asia/Dubai", label: "UAE (UTC+4)" },
  { value: "Asia/Karachi", label: "Pakistan (UTC+5)" },
  { value: "Asia/Kolkata", label: "India (UTC+5:30)" },
  { value: "Asia/Dhaka", label: "Bangladesh (UTC+6)" },
  { value: "Asia/Yangon", label: "Myanmar (UTC+6:30)" },
  { value: "Asia/Bangkok", label: "Thailand (UTC+7)" },
  { value: "Asia/Shanghai", label: "China (UTC+8)" },
  { value: "Asia/Tokyo", label: "Japan (UTC+9)" },
  { value: "Australia/Sydney", label: "Sydney (UTC+10)" },
  { value: "Pacific/Fiji", label: "Fiji (UTC+12)" },
];

const getCurrentTimeLabel = (timeZone) => {
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(new Date());
  } catch (e) {
    return null;
  }
};

const addCurrentTimeToLabel = (option) => {
  const currentTime = getCurrentTimeLabel(option.value);
  return {
    value: option.value,
    label: currentTime ? `${option.label} — ${currentTime}` : option.label,
  };
};

/**
 * The active timezone list — automatically selects DST or non-DST labels
 * based on whether daylight saving time is currently in effect.
 */
export const TIMEZONE_OPTIONS = (isDSTActive() ? TIMEZONE_OPTIONS_DST : TIMEZONE_OPTIONS_NO_DST).map(addCurrentTimeToLabel);

export const DEFAULT_TIMEZONE = "America/New_York";

// Linguist Portal access — true only when REACT_APP_ALLOW_LINGUIST_PORTAL_ACCESS="true"
// (set in dev only; undefined in prod builds → false until the portal launches in prod).
export const ALLOW_LINGUIST_PORTAL_ACCESS =
  process.env.REACT_APP_ALLOW_LINGUIST_PORTAL_ACCESS === "true";

export const COUNTRY_OPTIONS = [
  { value: "AF", label: "Afghanistan" },
  { value: "AL", label: "Albania" },
  { value: "DZ", label: "Algeria" },
  { value: "AR", label: "Argentina" },
  { value: "AM", label: "Armenia" },
  { value: "AU", label: "Australia" },
  { value: "AT", label: "Austria" },
  { value: "AZ", label: "Azerbaijan" },
  { value: "BD", label: "Bangladesh" },
  { value: "BH", label: "Bahrain" },
  { value: "DJ", label: "Djibouti" },
  { value: "KM", label: "Comoros" },
  { value: "BY", label: "Belarus" },
  { value: "BE", label: "Belgium" },
  { value: "BO", label: "Bolivia" },
  { value: "BA", label: "Bosnia and Herzegovina" },
  { value: "BR", label: "Brazil" },
  { value: "BG", label: "Bulgaria" },
  { value: "KH", label: "Cambodia" },
  { value: "CM", label: "Cameroon" },
  { value: "CA", label: "Canada" },
  { value: "CL", label: "Chile" },
  { value: "CN", label: "China" },
  { value: "CO", label: "Colombia" },
  { value: "CR", label: "Costa Rica" },
  { value: "HR", label: "Croatia" },
  { value: "CU", label: "Cuba" },
  { value: "CY", label: "Cyprus" },
  { value: "CZ", label: "Czech Republic" },
  { value: "CD", label: "DR Congo" },
  { value: "DK", label: "Denmark" },
  { value: "DO", label: "Dominican Republic" },
  { value: "EC", label: "Ecuador" },
  { value: "EG", label: "Egypt" },
  { value: "SV", label: "El Salvador" },
  { value: "EE", label: "Estonia" },
  { value: "ET", label: "Ethiopia" },
  { value: "FI", label: "Finland" },
  { value: "FR", label: "France" },
  { value: "GE", label: "Georgia" },
  { value: "DE", label: "Germany" },
  { value: "GH", label: "Ghana" },
  { value: "GR", label: "Greece" },
  { value: "GT", label: "Guatemala" },
  { value: "HT", label: "Haiti" },
  { value: "HN", label: "Honduras" },
  { value: "HK", label: "Hong Kong" },
  { value: "HU", label: "Hungary" },
  { value: "IS", label: "Iceland" },
  { value: "IN", label: "India" },
  { value: "ID", label: "Indonesia" },
  { value: "IR", label: "Iran" },
  { value: "IQ", label: "Iraq" },
  { value: "IE", label: "Ireland" },
  { value: "PS", label: "Palestine" },
  { value: "IT", label: "Italy" },
  { value: "JM", label: "Jamaica" },
  { value: "JP", label: "Japan" },
  { value: "JO", label: "Jordan" },
  { value: "KZ", label: "Kazakhstan" },
  { value: "KE", label: "Kenya" },
  { value: "KW", label: "Kuwait" },
  { value: "KG", label: "Kyrgyzstan" },
  { value: "LA", label: "Laos" },
  { value: "LV", label: "Latvia" },
  { value: "LB", label: "Lebanon" },
  { value: "LY", label: "Libya" },
  { value: "LT", label: "Lithuania" },
  { value: "LU", label: "Luxembourg" },
  { value: "MG", label: "Madagascar" },
  { value: "MY", label: "Malaysia" },
  { value: "MR", label: "Mauritania" },
  { value: "MA", label: "Morocco" },
  { value: "MM", label: "Myanmar" },
  { value: "MZ", label: "Mozambique" },
  { value: "NA", label: "Namibia" },
  { value: "NP", label: "Nepal" },
  { value: "NL", label: "Netherlands" },
  { value: "NZ", label: "New Zealand" },
  { value: "NI", label: "Nicaragua" },
  { value: "NG", label: "Nigeria" },
  { value: "MK", label: "North Macedonia" },
  { value: "NO", label: "Norway" },
  { value: "PK", label: "Pakistan" },
  { value: "OM", label: "Oman" },
  { value: "PA", label: "Panama" },
  { value: "PG", label: "Papua New Guinea" },
  { value: "PY", label: "Paraguay" },
  { value: "PE", label: "Peru" },
  { value: "PH", label: "Philippines" },
  { value: "PL", label: "Poland" },
  { value: "PT", label: "Portugal" },
  { value: "PR", label: "Puerto Rico" },
  { value: "QA", label: "Qatar" },
  { value: "RO", label: "Romania" },
  { value: "RU", label: "Russia" },
  { value: "RW", label: "Rwanda" },
  { value: "SA", label: "Saudi Arabia" },
  { value: "SD", label: "Sudan" },
  { value: "SO", label: "Somalia" },
  { value: "SN", label: "Senegal" },
  { value: "RS", label: "Serbia" },
  { value: "SG", label: "Singapore" },
  { value: "SK", label: "Slovakia" },
  { value: "SI", label: "Slovenia" },
  { value: "ZA", label: "South Africa" },
  { value: "KR", label: "South Korea" },
  { value: "ES", label: "Spain" },
  { value: "LK", label: "Sri Lanka" },
  { value: "SE", label: "Sweden" },
  { value: "CH", label: "Switzerland" },
  { value: "SY", label: "Syria" },
  { value: "TW", label: "Taiwan" },
  { value: "TJ", label: "Tajikistan" },
  { value: "TZ", label: "Tanzania" },
  { value: "TH", label: "Thailand" },
  { value: "TN", label: "Tunisia" },
  { value: "TR", label: "Turkey" },
  { value: "UG", label: "Uganda" },
  { value: "YE", label: "Yemen" },
  { value: "UA", label: "Ukraine" },
  { value: "AE", label: "United Arab Emirates" },
  { value: "GB", label: "United Kingdom" },
  { value: "US", label: "United States" },
  { value: "UY", label: "Uruguay" },
  { value: "UZ", label: "Uzbekistan" },
  { value: "VE", label: "Venezuela" },
  { value: "VN", label: "Vietnam" },
  { value: "WS", label: "Samoa" },
  { value: "ME", label: "Montenegro" },
  { value: "ZM", label: "Zambia" },
  { value: "ZW", label: "Zimbabwe" },
  { value: "BW", label: "Botswana" },
  { value: "MU", label: "Mauritius" },
  { value: "FJ", label: "Fiji" },
  { value: "SB", label: "Solomon Islands" },
  { value: "TO", label: "Tonga" },
  { value: "VU", label: "Vanuatu" },
  { value: "MT", label: "Malta" },
  { value: "MX", label: "Mexico" },
  { value: "AO", label: "Angola" },
].sort((a, b) => a.label.localeCompare(b.label));

export const CONST = {
  INTERPRETATION_FILTER_KEY: "INTERPRETATION_FILTER_KEY",
  TRANSLATION_FILTER_KEY: "TRANSLATION_FILTER_KEY",
  OTHER_FILTER_KEY: "OTHER_FILTER_KEY",
  INVOICES_FILTER_KEY: "INVOICES_FILTER_KEY",
  SERVICE_FILTER_KEY: "SERVICE_FILTER_KEY",

  INTERPRETATION_SORT_KEY: "INTERPRETATION_SORT_KEY",
  TRANSLATION_SORT_KEY: "TRANSLATION_SORT_KEY",
  OTHER_SORT_KEY: "OTHER_SORT_KEY",
  INVOICES_SORT_KEY: "INVOICES_SORT_KEY",
  SERVICE_SORT_KEY: "SERVICE_SORT_KEY",

  INTERPRETATION_MONTHS_KEY: "INTERPRETATION_MONTHS_KEY",
  INTERPRETATION_YEAR_KEY: "INTERPRETATION_YEAR_KEY",
  REPORTS_MONTHS_KEY: "REPORTS_MONTHS_KEY",
  REPORTS_YEAR_KEY: "REPORTS_YEAR_KEY",
  INTERPRETATIONS_SHOW_TOTALS_KEY: "SERVICE_SHOW_TOTALS_KEY",
  CLIENT_PROFILE_SHOW_TOTALS_KEY: "SERVICE_SHOW_TOTALS_KEY",
  LINGUIST_PROFILE_SHOW_TOTALS_KEY: "SERVICE_SHOW_TOTALS_KEY",

  TRANSLATION_MONTHS_KEY: "TRANSLATION_MONTHS_KEY",
  TRANSLATION_YEAR_KEY: "TRANSLATION_YEAR_KEY",
  TRANSLATIONS_SHOW_TOTALS_KEY: "SERVICE_SHOW_TOTALS_KEY",

  SERVICE_MONTHS_KEY: "SERVICE_MONTHS_KEY",
  SERVICE_YEAR_KEY: "SERVICE_YEAR_KEY",
  SERVICES_FOOTER_VIEW_KEY: "SERVICES_FOOTER_VIEW_KEY",

  OTHER_SERVICE_MONTHS_KEY: "OTHER_SERVICE_MONTHS_KEY",
  OTHER_SERVICE_YEAR_KEY: "OTHER_SERVICE_YEAR_KEY",
  OTHER_SERVICES_SHOW_TOTALS_KEY: "SERVICE_SHOW_TOTALS_KEY",

  INVOICES_MONTHS_KEY: "INVOICES_MONTHS_KEY",
  INVOICES_YEAR_KEY: "INVOICES_YEAR_KEY",

  INTERPRETATION_LINGUIST_FILTER_KEY: "INTERPRETATION_LINGUIST_FILTER_KEY",
  INTERPRETATION_LINGUIST_SORT_KEY: "INTERPRETATION_LINGUIST_SORT_KEY",
  INTERPRETATIONS_LINGUIST_COLUMNS_VISIBLE: "INTERPRETATIONS_LINGUIST_COLUMNS_VISIBLE",
  INTERPRETATIONS_LINGUIST_GRID_DENSITY: "INTERPRETATIONS_LINGUIST_GRID_DENSITY",
};
